// High-level crypto engine for WhatsApp-style group encryption.
// Uses WebCrypto where possible + noble for Ed25519 signatures and X25519 ECDH.

import * as ed25519 from "@noble/ed25519";
import { x25519 } from "@noble/curves/ed25519.js";

// Setup SHA-512 for @noble/ed25519 v3 in browser environment (one-time initialization)
// In v3.0.0, the hash setup is via ed25519.hashes.sha512Async (not utils.sha512Async)
ed25519.hashes.sha512Async = async (...messages: Uint8Array[]): Promise<Uint8Array> => {
  const totalLength = messages.reduce((acc, m) => acc + m.length, 0);
  const combined = new Uint8Array(totalLength);
  let offset = 0;
  for (const msg of messages) {
    combined.set(msg, offset);
    offset += msg.length;
  }
  const hashBuffer = await crypto.subtle.digest("SHA-512", combined);
  return new Uint8Array(hashBuffer);
};

export interface IdentityKeyPair {
  ed25519PublicKey: Uint8Array;
  ed25519PrivateKey: Uint8Array;
  x25519PublicKey: Uint8Array;
  x25519PrivateKey: Uint8Array;
}

export interface SenderRatchetState {
  chainKey: Uint8Array; // current CK_i
  nextMessageIndex: number;
}

export interface DerivedMessageKey {
  messageKey: Uint8Array;   // MK_i
  nextChainKey: Uint8Array; // CK_{i+1}
}

export interface SignedCiphertext {
  nonce: Uint8Array;
  ciphertext: Uint8Array;
  signature: Uint8Array;
}

// ---- helpers ----

async function hkdfSha256(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: Uint8Array,
  length: number
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    "raw",
    ikm.buffer as ArrayBuffer,
    "HKDF",
    false,
    ["deriveBits"]
  );

  const params: HkdfParams = {
    name: "HKDF",
    hash: "SHA-256",
    salt: salt.buffer as ArrayBuffer,
    info: info.buffer as ArrayBuffer,
  };

  const bits = (await crypto.subtle.deriveBits(
    params,
    key,
    length * 8
  )) as ArrayBuffer;

  return new Uint8Array(bits);
}

async function hmacSha256(keyBytes: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    "raw",
    keyBytes.buffer as ArrayBuffer,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );

  const sig = await crypto.subtle.sign(
    { name: "HMAC" },
    key,
    data.buffer as ArrayBuffer
  );

  return new Uint8Array(sig);
}

// ---- Identity / DH keys ----

export async function generateIdentityKeyPair(): Promise<IdentityKeyPair> {
  // Ed25519 for signatures (use async keygen API in v3)
  const { secretKey: ed25519PrivateKey, publicKey: ed25519PublicKey } =
    await ed25519.keygenAsync();

  // X25519 for Diffie–Hellman
  const x25519PrivateKey = x25519.utils.randomSecretKey();
  const x25519PublicKey = x25519.getPublicKey(x25519PrivateKey);

  return {
    ed25519PublicKey: new Uint8Array(ed25519PublicKey),
    ed25519PrivateKey: new Uint8Array(ed25519PrivateKey),
    x25519PublicKey,
    x25519PrivateKey,
  };
}

export async function deriveSessionKey(
  localX25519PrivateKey: Uint8Array,
  remoteX25519PublicKey: Uint8Array
): Promise<Uint8Array> {
  // Raw X25519 shared secret
  const shared = x25519.getSharedSecret(
    localX25519PrivateKey,
    remoteX25519PublicKey
  );

  // Derive a 32-byte session key using HKDF-SHA256
  const salt = new Uint8Array(32); // protocol-wide salt; all zeros for now
  const info = new TextEncoder().encode("session-key");

  return hkdfSha256(shared, salt, info, 32);
}

// ---- Sender-key ratchet (per user, per room) ----

export async function deriveMessageKey(
  currentChainKey: Uint8Array,
  messageIndex: number
): Promise<DerivedMessageKey> {
  // For simplicity we derive one step from currentChainKey.
  // Higher-level code is responsible for tracking messageIndex
  // and updating the chainKey each time.
  const labelMk = new TextEncoder().encode("MSG");
  const labelCk = new TextEncoder().encode("CK");

  const messageKey = await hmacSha256(currentChainKey, labelMk);
  const nextChainKey = await hmacSha256(currentChainKey, labelCk);

  return {
    messageKey,
    nextChainKey,
  };
}

// ---- Signing helpers (Ed25519) ----

export async function signBytes(
  privateKey: Uint8Array,
  data: Uint8Array
): Promise<Uint8Array> {
  const sig = await ed25519.signAsync(data, privateKey);
  return new Uint8Array(sig);
}

export async function verifySignature(
  publicKey: Uint8Array,
  data: Uint8Array,
  signature: Uint8Array
): Promise<boolean> {
  return ed25519.verifyAsync(signature, data, publicKey);
}

// ---- Sender Key Wrapping (X25519 + AES-GCM) ----

export interface WrappedSenderKey {
  nonce: Uint8Array;              // AES-GCM nonce
  encryptedKey: Uint8Array;       // Encrypted sender chain key
  signature: Uint8Array;          // Ed25519 signature over (nonce || encryptedKey)
}

/**
 * Wrap a sender chain key for a specific recipient using X25519 ECDH + AES-GCM.
 * Returns encrypted key, nonce, and Ed25519 signature.
 * Signs: roomId || senderUserId || recipientUserId || senderKeyId || nonce || encryptedKey
 */
export async function wrapSenderKey(
  senderChainKey: Uint8Array,
  localX25519PrivateKey: Uint8Array,
  recipientX25519PublicKey: Uint8Array,
  localEd25519PrivateKey: Uint8Array,
  sessionId: string,
  senderUserId: string,
  recipientUserId: string,
  senderKeyId: string
): Promise<WrappedSenderKey> {
  // 1. Derive session key via X25519 ECDH
  const sessionKey = await deriveSessionKey(
    localX25519PrivateKey,
    recipientX25519PublicKey
  );

  // 2. Encrypt sender chain key with AES-GCM
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const key = await crypto.subtle.importKey(
    "raw",
    sessionKey.buffer as ArrayBuffer,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );

  const encryptedKeyBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce.buffer as ArrayBuffer },
    key,
    senderChainKey.buffer as ArrayBuffer
  );
  const encryptedKey = new Uint8Array(encryptedKeyBuf);

  // 3. Sign (roomId || senderUserId || recipientUserId || senderKeyId || nonce || encryptedKey) with Ed25519
  const encoder = new TextEncoder();
  const sessionIdBytes = encoder.encode(sessionId);
  const senderUserIdBytes = encoder.encode(senderUserId);
  const recipientUserIdBytes = encoder.encode(recipientUserId);
  const senderKeyIdBytes = encoder.encode(senderKeyId);
  
  const signaturePayload = new Uint8Array(
    sessionIdBytes.length + 
    senderUserIdBytes.length + 
    recipientUserIdBytes.length + 
    senderKeyIdBytes.length + 
    nonce.length + 
    encryptedKey.length
  );
  
  let offset = 0;
  signaturePayload.set(sessionIdBytes, offset);
  offset += sessionIdBytes.length;
  signaturePayload.set(senderUserIdBytes, offset);
  offset += senderUserIdBytes.length;
  signaturePayload.set(recipientUserIdBytes, offset);
  offset += recipientUserIdBytes.length;
  signaturePayload.set(senderKeyIdBytes, offset);
  offset += senderKeyIdBytes.length;
  signaturePayload.set(nonce, offset);
  offset += nonce.length;
  signaturePayload.set(encryptedKey, offset);
  
  const signature = await signBytes(localEd25519PrivateKey, signaturePayload);

  return { nonce, encryptedKey, signature };
}

/**
 * Unwrap a sender chain key that was encrypted for us.
 * Verifies Ed25519 signature, then decrypts with X25519 session key.
 * Verifies: roomId || senderUserId || recipientUserId || senderKeyId || nonce || encryptedKey
 */
export async function unwrapSenderKey(
  encryptedKey: Uint8Array,
  nonce: Uint8Array,
  signature: Uint8Array,
  senderX25519PublicKey: Uint8Array,
  senderEd25519PublicKey: Uint8Array,
  localX25519PrivateKey: Uint8Array,
  sessionId: string,
  senderUserId: string,
  recipientUserId: string,
  senderKeyId: string
): Promise<Uint8Array> {
  // 1. Verify signature over (roomId || senderUserId || recipientUserId || senderKeyId || nonce || encryptedKey)
  const encoder = new TextEncoder();
  const sessionIdBytes = encoder.encode(sessionId);
  const senderUserIdBytes = encoder.encode(senderUserId);
  const recipientUserIdBytes = encoder.encode(recipientUserId);
  const senderKeyIdBytes = encoder.encode(senderKeyId);
  
  const signaturePayload = new Uint8Array(
    sessionIdBytes.length + 
    senderUserIdBytes.length + 
    recipientUserIdBytes.length + 
    senderKeyIdBytes.length + 
    nonce.length + 
    encryptedKey.length
  );
  
  let offset = 0;
  signaturePayload.set(sessionIdBytes, offset);
  offset += sessionIdBytes.length;
  signaturePayload.set(senderUserIdBytes, offset);
  offset += senderUserIdBytes.length;
  signaturePayload.set(recipientUserIdBytes, offset);
  offset += recipientUserIdBytes.length;
  signaturePayload.set(senderKeyIdBytes, offset);
  offset += senderKeyIdBytes.length;
  signaturePayload.set(nonce, offset);
  offset += nonce.length;
  signaturePayload.set(encryptedKey, offset);
  
  const isValid = await verifySignature(
    senderEd25519PublicKey,
    signaturePayload,
    signature
  );
  
  if (!isValid) {
    throw new Error("Sender key announcement signature verification failed");
  }

  // 2. Derive session key via X25519 ECDH
  const sessionKey = await deriveSessionKey(
    localX25519PrivateKey,
    senderX25519PublicKey
  );

  // 3. Decrypt sender chain key with AES-GCM
  const key = await crypto.subtle.importKey(
    "raw",
    sessionKey.buffer as ArrayBuffer,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );

  const decryptedKeyBuf = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonce.buffer as ArrayBuffer },
    key,
    encryptedKey.buffer as ArrayBuffer
  );

  return new Uint8Array(decryptedKeyBuf);
}